import React from 'react';
import { lastEditOf, describeLastEdit } from '../lib/last-edited';

/**
 * A quiet one-line "who touched this last, and when" stamp for ticket views.
 *
 * Tickets are last-write-wins with no locking and no merge, and the field app
 * can sync an offline edit an hour after the fact. This line is the only thing
 * that makes such an overwrite visible to a human. It is context, not a
 * headline — it should never compete with the ticket content.
 *
 * It is informational only. Do not grow it into conflict detection, an "edited
 * while you were viewing" warning, or a lock; that is a separate design
 * decision. See src/lib/last-edited.ts for the null rules.
 */

interface LastEditedStampProps {
  /** Raw API ticket. snake_case keys are read directly off it. */
  ticket?: any;
  /** Or pass the two halves explicitly. */
  name?: string | null;
  updatedAt?: string | null;
  /**
   * Where the absolute time goes. 'hover' puts it in the title attribute and
   * suits pointer devices; 'inline' prints it after the relative time and is
   * what touch-only screens need, since they have no hover.
   */
  absolute?: 'hover' | 'inline';
  className?: string;
}

export default function LastEditedStamp({
  ticket,
  name,
  updatedAt,
  absolute = 'hover',
  className = '',
}: LastEditedStampProps) {
  const display = ticket
    ? lastEditOf(ticket)
    : describeLastEdit(name, updatedAt);

  return (
    <p
      className={`text-xs text-fieldiq-muted ${className}`}
      // Absent when there is no instant, so hovering never reveals a blank tip.
      title={display.absolute ?? undefined}
      data-testid="last-edited-stamp"
      data-stamp-kind={display.kind}
    >
      {display.text}
      {absolute === 'inline' && display.absolute && (
        <span className="opacity-70"> ({display.absolute})</span>
      )}
    </p>
  );
}
